import NumberWithHoverPoints from '@/components/NumberWithHoverPoint'
import OperatorsIcon from '@/components/OperatorIcons'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DataTable } from '@/components/ui/data-table'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import UserFilter from '@/components/user-filter'
import { operators } from '@/constants/operators'
import { cancelScheduleTopup } from '@/queries/scheduledTopups'
import { convertDateTime } from '@/services/utlis'
import type { OperatorsName } from '@/stores/operator-store'
import { ScheduledStatus } from '@/types'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { PaginationState } from '@tanstack/react-table'
import { Check, Clock, Loader2, Trash, X } from 'lucide-react'
import { useState, type ReactNode, useMemo } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/queues')({
  component: QueuesPage,
})

function QueuesPage() {
  const [pagination, setPagination] = useState<PaginationState>({pageIndex: 0, pageSize: 10})
  // const [data, setData] = useState<Array<any>>([])
  const [filtered, setFiltered] = useState<any>()
  const [meta, setMeta] = useState<any>({})
  const [cancelConfirm, setCancelConfirm] = useState(false)
  const [selectedTopup, setSelectedTopup] = useState<any>(null)

  const scheduledCanceledMutation = useMutation({
    mutationFn: () => cancelScheduleTopup(selectedTopup),
    mutationKey: ['cancelTopup'],
    onSuccess: () => {
      toast.success('خرید با موفقیت لغو شد')
      setCancelConfirm(false)
      // scheduledListQuery.refetch()
    },
    onError() {
      toast.error('خطا در لغو خرید')
      setCancelConfirm(false)
    },
  })

  // useEffect(()=> {
  //   if (scheduledListQuery.data) {
  //     setData(scheduledListQuery.data.data.data.map((row:any)=> {
  //       if (typeof row.payload === 'string'){
  //         return {...row, payload: JSON.parse(row.payload)}
  //       }
  //       return row
  //     }))
  //     setMeta(scheduledListQuery.data.data.meta)
  //   }
  // }, [scheduledListQuery.data])

  const statusIcons:Record<keyof typeof ScheduledStatus, ReactNode> = {
    'pending': <Clock className='size-4 shrink-0' />,
    'canceled': <X className='size-4 shrink-0' />,
    'done':<Check className='size-4 shrink-0' />,
    'processing': <Loader2 className='size-4 shrink-0 animate-spin duration-1000'/>
  }

  const statusColors:Record<keyof typeof ScheduledStatus, string> = {
    'canceled': 'text-red-500',
    'done': 'text-green-500',
    'pending': 'text-yellow-500',
    'processing': 'text-blue-500'
  } 

  const columns = [
    {
      header: 'درخواست',
      id: 'request',
      cell: ({row}: any) => {
        const request = row.original
        const status = request.status as keyof typeof ScheduledStatus

        return (
          <div className='space-y-1.5 text-xs'>
            <div className='text-muted-foreground'>
              شماره <span className='text-foreground'>{request.id}</span>
            </div>
            <div className={`flex items-center gap-1 ${statusColors[status]}`}>
              {statusIcons[status]}
              <span>{ScheduledStatus[status]}</span>
            </div>
            {status === 'pending' && (
              <Button
                size='sm'
                variant='destructive'
                className='h-8 px-2'
                onClick={() =>{
                  setSelectedTopup(request.id)
                  setCancelConfirm(true)
                }}
              >
                <Trash className='ml-1 size-4'/>
                لغو
              </Button>
            )}
          </div>
        )
      },
    },
    {
      header: 'مشترک',
      id: 'subscriber',
      cell: ({row}: any) => {
        const payload = row.original.payload
        const product = payload?.[0]
        const operator = operators.find(op => product?.operator_id === op.id)
        const mobile = payload?.[1]

        return (
          <div className='space-y-1.5'>
            <div dir='ltr' className='text-right text-xs tabular-nums'>
              {mobile ? `0${mobile.slice(2)}` : '-'}
            </div>
            {operator ? (
              <div className='flex items-center gap-1.5 text-xs text-muted-foreground'>
                <OperatorsIcon
                  operator={operator.name as OperatorsName}
                  className='size-7 shrink-0'
                  operatorColors
                />
                <span>{operator.title}</span>
              </div>
            ) : (
              <span className='text-xs text-muted-foreground'>-</span>
            )}
          </div>
        )
      },
    },
    {
      header: 'محصول و مبلغ',
      id: 'product',
      cell: ({row}: any) => {
        const payload = row.original.payload
        const product = payload?.[0]
        const productDetails = [
          product?.name,
          product?.category_name,
          product?.sim_card_type
            ? product.sim_card_type === 'credit' ? 'اعتباری' : 'دائمی'
            : null,
        ].filter(Boolean).join(' - ')

        return (
          <div className='space-y-1.5 text-xs leading-5'>
            <div className='break-words font-medium'>{productDetails || '-'}</div>
            <div>
              <span className='text-muted-foreground'>مبلغ: </span>
              {payload?.[2] ? <NumberWithHoverPoints number={payload[2]}/> : '-'}
            </div>
          </div>
        )
      },
    },
    {
      header: 'زمان‌ها',
      id: 'dates',
      cell: ({row}: any) => {
        const request = row.original

        return (
          <div className='space-y-1.5 text-xs leading-5'>
            <div>
              <div className='text-muted-foreground'>زمان انجام</div>
              <div dir='ltr' className='text-right tabular-nums'>
                {convertDateTime(new Date(request.scheduled_at.replace('-', '/')))}
              </div>
            </div>
            <div>
              <div className='text-muted-foreground'>زمان ایجاد</div>
              <div dir='ltr' className='text-right tabular-nums'>
                {convertDateTime(new Date(request.created_at))}
              </div>
            </div>
          </div>
        )
      },
    },
  ]

  const data = useMemo(()=> {
    if (!filtered) {
      return []
    }
    return filtered
  }, [filtered])

  return (<>
  <Dialog open={cancelConfirm} onOpenChange={setCancelConfirm}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>لغو خرید</DialogTitle>
      </DialogHeader>
      <p>ایا از انجام این عملیات مطمئن هستید ؟ </p>
      <DialogFooter>
        <Button variant="secondary" onClick={() => setCancelConfirm(false)}>
          لغو
        </Button>
        <Button onClick={() => scheduledCanceledMutation.mutate()} isLoading={scheduledCanceledMutation.isPending}>
          تایید
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  <Card>
    <CardHeader className='p-3 lg:p-6'>
      <CardTitle className='text-base lg:text-2xl'>
        صف خرید 
      </CardTitle>
    </CardHeader>
    <CardContent className='p-2 pt-0 lg:p-6 lg:pt-0'>
      <UserFilter table='scheduledTopup' filtered={filtered} setFiltered={(data:any) => {
        setFiltered(data)
      }} setMeta={setMeta} page={pagination.pageIndex} perPage={pagination.pageSize}  />
      {filtered && (
        <div className='[&_table]:table-fixed [&_th]:h-10 [&_th]:px-2 [&_td]:p-2'>
          <DataTable<any,any> columns={columns} data={data} pageCount={meta?.last_page || 1} pagination={pagination} onPaginationChange={setPagination}/>
        </div>
      )}
    </CardContent>
  </Card>
  </>)
}
